import { useState, useCallback } from 'react';
import { executeAzureUploadFlow } from './azureUploadFlow';

/**
 * Generic hook for uploading a file to Azure Blob Storage in 3 phases:
 *   - 'preparing': fetches the SAS URL and extracts a thumbnail in parallel
 *   - 'uploading': PUT of the file directly to Azure
 *   - 'saving':    calls onRegister to persist the resource in the backend
 *
 * Usage:
 *   const { uploadPhase, uploadProgress, uploadError, clearUploadError, executeUpload } = useAzureUpload();
 *
 *   await executeUpload({
 *     file,        // File to upload
 *     prefix,      // Blob path prefix for the main file  (e.g. 'videos/incoming/')
 *     thumbPrefix, // Blob path prefix for the thumbnail (e.g. 'thumbnails/') — optional
 *     shouldExtractThumbnail, // optional: defaults true; set false for non-video uploads
 *     onRegister,  // async ({ blobName, thumbnailBlobName }) => void
 *     onProgress,  // optional: (pct: number) => void
 *   });
 *
 * uploadError distinguishes two failure scenarios:
 *   - Failure before the PUT: original error message
 *   - Failure after the PUT (onRegister): fixed message directing the user to support
 */
export function useAzureUpload() {
    const [uploadPhase, setUploadPhase] = useState(null); // null | 'preparing' | 'uploading' | 'saving'
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState('');

    const executeUpload = useCallback(async ({
        file,
        prefix,
        thumbPrefix,
        shouldExtractThumbnail = true,
        onRegister,
        onProgress
    }) => {
        let enteredSavingPhase = false;
        setUploadError('');
        setUploadProgress(0);

        try {
            await executeAzureUploadFlow({
                file,
                prefix,
                thumbPrefix,
                shouldExtractThumbnail,
                onRegister,
                onProgress: (pct) => {
                    setUploadProgress(pct);
                    if (onProgress) onProgress(pct);
                },
                onPhaseChange: (phase) => {
                    if (phase === 'saving') enteredSavingPhase = true;
                    setUploadPhase(phase);
                }
            });
        } catch (err) {
            const message = enteredSavingPhase
                ? 'File uploaded but registration failed — contact the support team.'
                : (err.message || 'Upload failed.');
            setUploadError(message);
            throw err;
        } finally {
            setUploadPhase(null);
            setUploadProgress(0);
        }
    }, []);

    const clearUploadError = useCallback(() => setUploadError(''), []);

    return { uploadPhase, uploadProgress, uploadError, clearUploadError, executeUpload };
}
